import MuiButton, { type ButtonProps as MuiButtonProps } from '@mui/material/Button';
import type { ElementType } from 'react';

type Variant = 'primary' | 'secondary' | 'ghost' | 'danger';
type Size = 'sm' | 'md';

/**
 * Accepts the props of whichever element it renders as, so
 * `<Button component={Link} to="/x" />` type-checks. This replaces the previous
 * `asChild` prop, which came from Radix; `component` is the MUI idiom.
 */
export type ButtonProps<C extends ElementType = 'button'> = Omit<
  MuiButtonProps<C, { component?: C }>,
  'variant' | 'size' | 'color'
> & {
  variant?: Variant;
  size?: Size;
};

/** Maps the portal's four intents onto MUI's variant and colour pair. */
const INTENT: Record<Variant, Pick<MuiButtonProps, 'variant' | 'color'>> = {
  primary: { variant: 'contained', color: 'primary' },
  secondary: { variant: 'outlined', color: 'primary' },
  ghost: { variant: 'text', color: 'inherit' },
  danger: { variant: 'contained', color: 'error' },
};

const SIZES: Record<Size, MuiButtonProps['size']> = {
  sm: 'small',
  md: 'medium',
};

export function Button<C extends ElementType = 'button'>({
  variant = 'primary',
  size = 'md',
  ...props
}: ButtonProps<C>) {
  // The default `type="button"` comes from the theme: a button inside a form
  // submits it unless told otherwise, which is a common source of accidental
  // submissions.
  return <MuiButton {...INTENT[variant]} size={SIZES[size]} {...props} />;
}
